import React, { useCallback, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import NumberedSection from '../components/selection/NumberedSection';
import GenerateBar from '../components/mapping/GenerateBar';
import OperationList from '../components/mapping/OperationList';
import SourceTabs, { SwaggerSource } from '../components/mapping/SourceTabs';
import SpecSummary from '../components/mapping/SpecSummary';
import SwaggerFileList from '../components/mapping/SwaggerFileList';
import SwaggerUrlField from '../components/mapping/SwaggerUrlField';
import TagFilter from '../components/mapping/TagFilter';
import UrlFilterFields from '../components/mapping/UrlFilterFields';
import WorkbookIncludes from '../components/mapping/WorkbookIncludes';
import { useMappingJob } from '../components/mapping/hooks/useMappingJob';
import { useSwaggerFiles } from '../components/swagger/useSwaggerFiles';
import { useSwaggerOutline } from '../components/mapping/hooks/useSwaggerOutline';
import {
  countOperationsByTag,
  isHttpUrl,
  operationsInTags,
  parseList,
  picksInScope,
  plural,
  scopeSummary,
} from '../components/mapping/mappingScope';

const NONE: ReadonlySet<string> = new Set();

const toggled = (set: ReadonlySet<string>, value: string): ReadonlySet<string> => {
  const next = new Set(set);
  if (!next.delete(value)) next.add(value);
  return next;
};

const pageHeader = (
  <Box sx={{ mb: 1 }}>
    <Typography component="h1" sx={{ fontSize: 32, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
      Mapping document
    </Typography>
    <Typography sx={{ mt: 0.75, maxWidth: 640, fontSize: 14, color: 'text.secondary' }}>
      Turn an OpenAPI/Swagger spec into an Excel workbook that walks customers through operations, paths and data schemas.
    </Typography>
  </Box>
);

const placeholderSx = { fontSize: 13, color: 'text.secondary', p: 1.5, border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 3 } as const;

const MappingDocumentPage: React.FC = () => {
  const { files, loading: filesLoading, loadError: filesError } = useSwaggerFiles();
  const job = useMappingJob();

  const [source, setSource] = useState<SwaggerSource>('uploaded');
  const [fileId, setFileId] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [urlTags, setUrlTags] = useState('');
  const [urlOperationIds, setUrlOperationIds] = useState('');
  const [selectedTags, setSelectedTags] = useState<ReadonlySet<string>>(NONE);
  const [picks, setPicks] = useState<ReadonlySet<string>>(NONE);

  const isUploaded = source === 'uploaded';
  const { outline, loading: outlineLoading, error: outlineError } = useSwaggerOutline(isUploaded ? fileId : null);

  const changeSource = useCallback((next: SwaggerSource) => {
    setSource(next);
    setSelectedTags(NONE);
    setPicks(NONE);
  }, []);

  const selectFile = useCallback(
    (id: string) => {
      if (id === fileId) return;
      setFileId(id);
      setSelectedTags(NONE);
      setPicks(NONE);
    },
    [fileId],
  );

  const toggleTag = useCallback((tag: string) => setSelectedTags((prev) => toggled(prev, tag)), []);
  const clearTags = useCallback(() => setSelectedTags(NONE), []);
  const togglePick = useCallback((id: string) => setPicks((prev) => toggled(prev, id)), []);
  const setManyPicks = useCallback((ids: string[], selected: boolean) => {
    setPicks((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (selected ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  const operations = outline?.operations;
  const tagOptions = useMemo(() => (outline ? countOperationsByTag(outline.tags, outline.operations) : []), [outline]);
  const inScope = useMemo(() => operationsInTags(operations ?? [], selectedTags), [operations, selectedTags]);
  const scopedPicks = useMemo(() => picksInScope(inScope, picks), [inScope, picks]);

  const file = isUploaded ? files.find((f) => f.id === fileId) : undefined;
  const specTitle = outline?.info.title || file?.originalName || '';
  const filtered = scopedPicks.length > 0 || selectedTags.size > 0;

  let hint: string | null = null;
  if (file) {
    const opsText = outline && filtered ? `${scopedPicks.length || inScope.length} of ${plural(outline.operations.length, 'operation')}` : 'all operations';
    hint = `${specTitle} · ${opsText} · .xlsx`;
  } else if (!isUploaded && isHttpUrl(url)) {
    const filterCount = parseList(urlTags).length + parseList(urlOperationIds).length;
    hint = `Remote spec · ${filterCount ? plural(filterCount, 'filter') : 'all operations'} · .xlsx`;
  }

  const handleGenerate = () => {
    if (isUploaded) {
      if (!fileId) return job.setError('Please select a swagger file');
      job.submit({ swaggerFileId: fileId, filterTags: Array.from(selectedTags), filterOperationIds: scopedPicks });
    } else {
      if (!url.trim()) return job.setError('Please enter a swagger URL');
      job.submit({ swaggerUrl: url.trim(), filterTags: parseList(urlTags), filterOperationIds: parseList(urlOperationIds) });
    }
  };

  let scopeContent: React.ReactNode;
  if (!isUploaded) {
    scopeContent = (
      <UrlFilterFields tags={urlTags} operationIds={urlOperationIds} onTagsChange={setUrlTags} onOperationIdsChange={setUrlOperationIds} />
    );
  } else if (!fileId) {
    scopeContent = <Typography sx={placeholderSx}>Choose a Swagger source to filter by tag or operation.</Typography>;
  } else if (outlineLoading) {
    scopeContent = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, fontSize: 13, color: 'text.secondary' }}>
        <CircularProgress size={18} />
        Loading tags and operations…
      </Box>
    );
  } else if (outlineError || !outline) {
    scopeContent = <Alert severity="error">{outlineError ?? 'Failed to load tags and operation IDs from swagger file'} — the document can still be generated with all operations.</Alert>;
  } else {
    scopeContent = (
      <>
        <TagFilter options={tagOptions} selected={selectedTags} onToggle={toggleTag} onClear={clearTags} />
        <OperationList operations={inScope} picks={picks} onToggle={togglePick} onSetMany={setManyPicks} />
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 976, width: '100%', minWidth: 0 }}>
      {pageHeader}

      {job.error && (
        <Alert severity="error" onClose={job.clearError}>
          {job.error}
        </Alert>
      )}

      <NumberedSection step={1} title="Swagger source" action={<SourceTabs value={source} onChange={changeSource} />}>
        {isUploaded ? (
          <SwaggerFileList files={files} loading={filesLoading} loadError={filesError} selectedId={fileId} onSelect={selectFile} />
        ) : (
          <SwaggerUrlField value={url} onChange={setUrl} />
        )}
        {isUploaded && outline && <SpecSummary title={specTitle} outline={outline} />}
      </NumberedSection>

      <Box sx={{ opacity: isUploaded && !fileId ? 0.6 : 1, transition: 'opacity 150ms' }}>
        <NumberedSection step={2} title="Scope" meta={isUploaded && outline ? scopeSummary(inScope.length, scopedPicks.length, selectedTags.size) : undefined}>
          {scopeContent}
        </NumberedSection>
      </Box>

      <WorkbookIncludes />

      <GenerateBar hint={hint} submitting={job.submitting} created={job.created} onSubmit={handleGenerate} />
    </Box>
  );
};

export default MappingDocumentPage;
